import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import {
  useGetGroupLessonAttendanceQuery,
  useCreateBulkAttendanceMutation,
  type IGroupLessonAttendanceStudent,
} from '@/entities/TeacherAttendance';
import { useGetModuleBaseInfoQuery, useGetTermsQuery } from '@/entities/Courses';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { AppButton } from '@/shared/ui/AppButton';
import { AppImage } from '@/shared/ui/AppImage';
import { Svg } from '@/shared/ui/Svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { formatDateTimeStored } from '@/shared/lib/utils/formatDate';
import { routerPath } from '@/shared/config/router';
import CloseSmIcon from '@/shared/assets/icons/close-sm.svg';
import { AttendanceStatusSelect } from '../components/AttendanceStatusSelect';
import styles from './SessionAttendanceView.module.scss';

type AttendanceValue = 0 | 0.5 | 1;

interface SessionAttendanceViewProps {
  courseId: number;
  intakeId: number;
  groupNumber: number;
  termId: number;
  moduleId: number;
  groupLessonId: number;
}

const getEnrollmentId = (student: IGroupLessonAttendanceStudent) =>
  student.enrollmentId ?? student.id;

const getPersonName = (person?: { firstName?: string; lastName?: string; username?: string } | null) => {
  if (!person) return '';
  const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return fullName || person.username || '';
};

const parseModuleHeading = (title: string, number?: number) => {
  const match = title.match(/^(\d+)\.\s*(.*)$/);
  if (match) {
    return { n: match[1], name: match[2] };
  }
  if (number != null) {
    return { n: String(number), name: title };
  }
  return { n: null, name: title };
};

export const SessionAttendanceView = ({
  courseId,
  intakeId,
  groupNumber,
  termId,
  moduleId,
  groupLessonId,
}: SessionAttendanceViewProps) => {
  const { toast } = useToast();
  const { canEntity } = useAdminAccess();
  // Посещаемость сессии сохраняется bulk-созданием: без attendance.create страница только для чтения
  const canSaveAttendance = canEntity('attendance', 'create');
  const { data, isLoading } = useGetGroupLessonAttendanceQuery({ groupLessonId });
  const [createBulkAttendance, { isLoading: isSaving }] = useCreateBulkAttendanceMutation();
  const [values, setValues] = useState<Record<number, AttendanceValue | null>>({});
  const [showValidation, setShowValidation] = useState(false);

  const { data: termsData } = useGetTermsQuery({ intakeId });
  const groupId = useMemo(() => {
    const group = termsData?.data?.groups.find((g) => g.number === groupNumber);
    return group?.id;
  }, [termsData?.data?.groups, groupNumber]);

  const { data: moduleBaseInfo } = useGetModuleBaseInfoQuery(
    { intakeModuleId: moduleId, groupId: groupId || 0 },
    { skip: !groupId },
  );

  useEffect(() => {
    if (!data?.students) return;
    const next: Record<number, AttendanceValue | null> = {};
    data.students.forEach((student) => {
      next[getEnrollmentId(student)] = student.attendance ?? 0;
    });
    setValues(next);
    setShowValidation(false);
  }, [data?.students]);

  const students = data?.students ?? [];
  const editableStudents = students.filter((student) => student.isEditable);
  const allLocked = students.length > 0 && editableStudents.length === 0;
  const canBulkCreate =
    students.length > 0 &&
    (allLocked || editableStudents.some((student) => student.attendanceId == null));

  const unmarkedCount = editableStudents.filter(
    (student) => values[getEnrollmentId(student)] === null || values[getEnrollmentId(student)] === undefined,
  ).length;
  const isComplete = unmarkedCount === 0;

  const lecturerName = getPersonName(data?.lecturer) || getPersonName(data?.moduleTeacher) || '—';
  const lecturerAvatar = data?.lecturer?.avatar || data?.moduleTeacher?.avatar || null;
  const moduleHeading = parseModuleHeading(data?.module.title || '', data?.module.number);

  const timeRange = useMemo(() => {
    const start = formatDateTimeStored(data?.lesson.scheduledAt ?? null);
    const end = formatDateTimeStored(data?.lesson.endDate ?? null);
    if (!start) return '—';
    if (!end) return `${start.date}, ${start.time}`;
    return `${start.date}, ${start.time} - ${end.time}`;
  }, [data?.lesson.scheduledAt, data?.lesson.endDate]);

  const breadcrumbItems = useMemo(() => {
    const courseTitle = moduleBaseInfo?.data?.course?.title;
    const intakeTitle = moduleBaseInfo?.data?.intake?.title;
    const termNumber = moduleBaseInfo?.data?.term?.number;
    const moduleTitle = data?.module.title || moduleBaseInfo?.data?.module?.title || 'Module';
    const lessonTitle = data?.lesson.title || 'Session';
    const groupDetailPath = routerPath.groupDetail
      .replace(':courseId', String(courseId))
      .replace(':intakeId', String(intakeId))
      .replace(':groupNumber', String(groupNumber));
    const termDetailPath = routerPath.termDetail
      .replace(':courseId', String(courseId))
      .replace(':intakeId', String(intakeId))
      .replace(':groupNumber', String(groupNumber))
      .replace(':termId', String(termId));
    const moduleDetailPath = routerPath.moduleDetail
      .replace(':courseId', String(courseId))
      .replace(':intakeId', String(intakeId))
      .replace(':groupNumber', String(groupNumber))
      .replace(':termId', String(termId))
      .replace(':moduleId', String(moduleId));

    return [
      { label: 'Courses', href: routerPath.courses },
      {
        label: courseTitle || 'Course',
        href: routerPath.courseDetail.replace(':id', String(courseId)),
      },
      {
        label: intakeTitle ? `${intakeTitle} - Group ${groupNumber}` : `Group ${groupNumber}`,
        href: groupDetailPath,
      },
      { label: termNumber ? `Term ${termNumber}` : 'Term', href: termDetailPath },
      { label: moduleTitle, href: moduleDetailPath },
      { label: lessonTitle },
    ];
  }, [
    moduleBaseInfo?.data?.course?.title,
    moduleBaseInfo?.data?.intake?.title,
    moduleBaseInfo?.data?.term?.number,
    moduleBaseInfo?.data?.module?.title,
    data?.module.title,
    data?.lesson.title,
    courseId,
    intakeId,
    groupNumber,
    termId,
    moduleId,
  ]);

  const handleChange = (enrollmentId: number, value: AttendanceValue) => {
    setValues((prev) => ({ ...prev, [enrollmentId]: value }));
  };

  const handleSave = async () => {
    if (!isComplete) {
      setShowValidation(true);
      return;
    }

    const payload = editableStudents
      .map((student) => {
        const enrollmentId = getEnrollmentId(student);
        const value = values[enrollmentId];
        if (value === null || value === undefined) return null;
        return { enrollmentId, value };
      })
      .filter((item): item is { enrollmentId: number; value: AttendanceValue } => item !== null);

    try {
      await createBulkAttendance({
        groupLessonId,
        students: allLocked ? [] : payload,
      }).unwrap();
      toast({
        title: 'Success',
        description: 'Attendance saved successfully',
        color: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to save attendance',
        color: 'danger',
      });
    }
  };

  if (isLoading) {
    return <SectionLoader />;
  }

  if (!data) {
    return null;
  }

  return (
    <div className={styles.root}>
      <Container className="flex flex-col gap-6">
        <CourseBreadcrumbs items={breadcrumbItems} />
        <h2 className={styles.pageTitle}>Session Attendance</h2>
        <h3 className={styles.lessonTitle}>{data.lesson.title}</h3>
        <div className={styles.meta}>
          <p className={styles.moduleLine}>
            Module:{' '}
            <span className={styles.moduleName}>
              {moduleHeading.n ? `${moduleHeading.n}. ${moduleHeading.name}` : moduleHeading.name}
            </span>
          </p>
          <p className={styles.timeLine}>{timeRange}</p>
          <div className={styles.lecturer}>
            {lecturerAvatar ? (
              <AppImage src={lecturerAvatar} alt={lecturerName} className={styles.avatar} />
            ) : (
              <span className={styles.avatarFallback} />
            )}
            {lecturerName}
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th className={styles.statusCol}>Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => {
                const enrollmentId = getEnrollmentId(student);
                const value = values[enrollmentId] ?? 0;
                return (
                  <tr key={enrollmentId} className={cn(!student.isEditable && styles.rowLocked)}>
                    <td className={styles.idCell}>
                      {student.courseIdentifier || enrollmentId}
                    </td>
                    <td>
                      <div className={styles.userCell}>
                        {student.avatar ? (
                          <AppImage
                            src={student.avatar}
                            alt={getPersonName(student)}
                            className={styles.userAvatar}
                          />
                        ) : (
                          <span className={styles.userAvatarFallback} />
                        )}
                        <span>{getPersonName(student)}</span>
                      </div>
                    </td>
                    <td className={styles.statusCol}>
                      <div className={styles.statusCell}>
                        {student.isEditable && canSaveAttendance ? (
                          <AttendanceStatusSelect
                            value={value}
                            onChange={(next) => handleChange(enrollmentId, next)}
                          />
                        ) : (
                          <span className={styles.lockedBadge}>
                            {(student.attendance ?? 0).toFixed(1)}
                            <Svg Icon={CloseSmIcon} width={12} height={12} />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {canBulkCreate && canSaveAttendance && (
          <div className={styles.footer}>
            {showValidation && !isComplete && (
              <p className={styles.validation}>Mark the attendance of all students</p>
            )}
            <AppButton
              color="red"
              onPress={handleSave}
              isLoading={isSaving}
              className={styles.saveBtn}
            >
              Save attendance
            </AppButton>
          </div>
        )}
      </Container>
    </div>
  );
};
